import type React from "react";
import { createContext, useMemo, type ReactNode } from "react";
import { createContextualCan, useAbility as useCaslAbility } from "@casl/react";
import { createMongoAbility, subject, type MongoAbility, type RawRuleOf } from "@casl/ability";
import { allSubjects, type TrellisSubjectKeys, type TrellisSubjects } from "./subjects";
import type { UserProfile } from "../../zodios/apiDefinitions/api/userProfile";
import { useCurrentUserProfile } from "../../modules/auth/api/useCurrentUserProfile";
import * as Sentry from "@sentry/react";
import { useAppSelector } from "../../redux/hooks";

type Actions = "manage" | "create" | "read" | "update" | "delete" | "export";
type AppAbility = MongoAbility<[Actions, TrellisSubjects | "all"]>;

const AbilityContext = createContext<AppAbility>(createMongoAbility());
/** @public */
export const Can = createContextualCan(AbilityContext.Consumer);

const useDefaultPermissionObject = <T extends TrellisSubjectKeys>(subjectType: T) => {
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const orgId = useAppSelector((state) => parseInt(state.user.organization_id, 10));

  return subject(subjectType, {
    org_id: orgId,
    market_place: marketPlace,
  });
};

export function SimpleCan(props: {
  action: Actions;
  subject: TrellisSubjectKeys;
  passThrough?: boolean;
  not?: boolean;
  children: ReactNode | ((isAllowed: boolean) => ReactNode);
}): ReactNode {
  let hasPermission = useSimpleCan(props.action, props.subject);
  hasPermission = props.not ? !hasPermission : hasPermission;

  if (props.passThrough && typeof props.children === "function") {
    return props.children(hasPermission);
  }

  return hasPermission ? <>{props.children}</> : null;
}

export function useSimpleCan(action: Actions, subject: TrellisSubjectKeys) {
  const defaultObject = useDefaultPermissionObject(subject);

  return useCan(action, defaultObject);
}

export const AbilityContextProvider = ({ children }: { children: React.ReactNode }) => {
  const { data } = useCurrentUserProfile();

  const ability = useMemo(() => {
    const rules = buildRules(data);
    return createMongoAbility<AppAbility>(rules);
  }, [data]);

  return <AbilityContext.Provider value={ability}>{children}</AbilityContext.Provider>;
};

export const useAbility = () => useCaslAbility(AbilityContext);
export const useCan = (...args: Parameters<AppAbility["can"]>) => useAbility().can(...args);
/** @public */
export const useCannot = (...args: Parameters<AppAbility["can"]>) => useAbility().cannot(...args);

function buildRules(user: UserProfile | undefined): RawRuleOf<AppAbility>[] {
  if (!user) {
    return [];
  }

  if (user.is_staff) {
    return [{ action: "manage", subject: "all" }];
  }

  const rules: RawRuleOf<AppAbility>[] = [];

  for (const membership of user.memberships) {
    const membershipOrgId = membership.membership_role_org_id;
    const permissions = membership.membership_role_permissions;

    const permissionToRuleset: Record<string, RawRuleOf<AppAbility>[]> = {
      admin_action: [
        {
          action: ["manage"],
          subject: ["Org", "User", "Billing", "Goal"],
          conditions: { org_id: membershipOrgId },
        },
      ],
      modify: [
        {
          action: ["create", "update", "delete"],
          subject: allSubjects.filter((subject) => !["Org", "User", "Billing", "Goal"].includes(subject)),
          conditions: { org_id: membershipOrgId },
        },
      ],
      read: [
        {
          action: "read",
          subject: allSubjects.filter((subject) => !["Billing"].includes(subject)),
          conditions: { org_id: membershipOrgId },
        },
      ],
      export: [
        {
          action: "export",
          subject: [...allSubjects],
          conditions: { org_id: membershipOrgId },
        },
      ],
    };

    for (const permission of permissions) {
      const permissions = permissionToRuleset[permission];

      if (permissions) {
        rules.push(...permissions);
      } else {
        Sentry.captureException(new Error(`Unknown permission: ${permission}`), { extra: { membership } });
      }
    }
  }

  return rules;
}
